import { useState, useRef, useEffect, useCallback } from 'react';
import { Routes, Route, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import {
  Tags,
  Plus,
  List,
  Download,
  Trash2,
  Edit,
  Copy,
  Layout,
  Upload,
  Image,
  Palette,
  FileText,
  FileSpreadsheet,
  Sparkles,
  Eye,
  RotateCcw,
  X,
  Link,
  Layers,
  Type,
  Grid3X3,
  Leaf,
  Droplet,
  Coffee,
  Cookie,
  Utensils,
  Sparkle,
  Heart,
  RectangleHorizontal,
  RectangleVertical,
  Square,
  Minus,
  Frame,
  Camera,
  Send,
  MessageCircle,
  Lock,
  Package,
  ChevronDown,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import QRCode from 'qrcode';

// Product type for import
interface Product {
  id: string;
  name: string;
  code?: string;
  price: number;
  currency: string;
  weight?: number;
  description?: string;
  imageUrl?: string;
  categoryName?: string;
  store?: { name: string };
}

// ============================================
// CONFIGURATION
// ============================================

type Category = {
  name: string;
  color: string;
  gradient: string;
  icon: LucideIcon;
  fields: string[];
};

type LayoutConfig = {
  name: string;
  width: number;
  height: number;
  icon: LucideIcon;
};

const CATEGORIES: Record<string, Category> = {
  CANNABIS: { 
    name: 'Cannabis', 
    color: '#22c55e', 
    gradient: 'from-green-500 to-emerald-600',
    icon: Leaf,
    fields: ['Strain', 'THC %', 'CBD %', 'Terpenes'] 
  },
  HASHISH: { 
    name: 'Extract', 
    color: '#f59e0b', 
    gradient: 'from-amber-500 to-orange-600',
    icon: Droplet,
    fields: ['Method', 'Texture', 'Potency', 'Origin'] 
  },
  COFFEE: { 
    name: 'Coffee', 
    color: '#78350f', 
    gradient: 'from-amber-800 to-amber-950',
    icon: Coffee,
    fields: ['Roast Level', 'Tasting Notes', 'Altitude', 'Process'] 
  },
  CACAO: { 
    name: 'Cacao', 
    color: '#92400e', 
    gradient: 'from-amber-700 to-yellow-900',
    icon: Cookie,
    fields: ['Cacao %', 'Variety', 'Origin', 'Flavor Notes'] 
  },
  GASTRO: { 
    name: 'Gastronomia', 
    color: '#3b82f6', 
    gradient: 'from-blue-500 to-cyan-600',
    icon: Utensils,
    fields: ['Storage', 'Expiry', 'Allergens', 'Ingredients'] 
  },
  COSMETICS: { 
    name: 'Cosmetics', 
    color: '#ec4899', 
    gradient: 'from-pink-500 to-rose-600',
    icon: Heart,
    fields: ['Skin Type', 'Volume', 'PAO', 'Ingredients'] 
  },
  CUSTOM: { 
    name: 'Custom', 
    color: '#6366f1', 
    gradient: 'from-indigo-500 to-purple-600',
    icon: Sparkle,
    fields: ['Field 1', 'Field 2', 'Field 3', 'Field 4'] 
  },
};

const LAYOUTS: Record<string, LayoutConfig> = {
  LANDSCAPE: { name: 'Landscape', width: 420, height: 280, icon: RectangleHorizontal },
  PORTRAIT: { name: 'Portrait', width: 280, height: 400, icon: RectangleVertical },
  SQUARE: { name: 'Square', width: 320, height: 320, icon: Square },
  SHELF: { name: 'Shelf Tag', width: 350, height: 150, icon: Minus },
  SOCIAL: { name: 'Social', width: 400, height: 400, icon: Frame },
};

const PLATFORMS = {
  NONE: { name: 'Standard', icon: null, badge: null },
  INSTAGRAM: { name: 'Instagram', icon: Camera, badge: '#E4405F' },
  TELEGRAM: { name: 'Telegram', icon: Send, badge: '#0088cc' },
  WHATSAPP: { name: 'WhatsApp', icon: MessageCircle, badge: '#25D366' },
  SIGNAL: { name: 'Signal', icon: Lock, badge: '#3A76F0' },
};

const TEMPLATES = [
  {
    id: 'cannabis-premium',
    name: 'Cannabis Premium',
    category: 'CANNABIS',
    layout: 'LANDSCAPE',
    primaryColor: '#22c55e',
    secondaryColor: '#064e3b',
    brand: 'GREENGOLD',
    slogan: 'Premium Quality',
  },
  {
    id: 'coffee-artisan',
    name: 'Artisan Coffee',
    category: 'COFFEE',
    layout: 'PORTRAIT',
    primaryColor: '#78350f',
    secondaryColor: '#fef3c7',
    brand: 'ROAST MASTERS',
    slogan: 'Single Origin',
  },
  {
    id: 'cacao-organic',
    name: 'Organic Cacao',
    category: 'CACAO',
    layout: 'SQUARE',
    primaryColor: '#92400e',
    secondaryColor: '#fcd34d',
    brand: 'BEAN TO BAR',
    slogan: 'Pure & Natural',
  },
  {
    id: 'extract-luxury',
    name: 'Luxury Extract',
    category: 'HASHISH',
    layout: 'LANDSCAPE',
    primaryColor: '#f59e0b',
    secondaryColor: '#1f2937',
    brand: 'GOLD STANDARD',
    slogan: 'Crafted Excellence',
  },
];

interface Label {
  id: string;
  name: string;
  category: keyof typeof CATEGORIES;
  sku?: string;
  price?: string;
  weight?: string;
  description?: string;
  brand?: string;
  slogan?: string;
  specs?: Record<string, string>;
  imageUrl?: string;
  layout: keyof typeof LAYOUTS;
  platform: keyof typeof PLATFORMS;
  qrEnabled: boolean;
  qrContent?: string;
  primaryColor: string;
  secondaryColor: string;
  showBorder: boolean;
  showGradient: boolean;
}

// ============================================
// LABEL EDITOR COMPONENT
// ============================================

function LabelEditor() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const previewRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'basic' | 'design' | 'qr' | 'export'>('basic');
  const [isDragging, setIsDragging] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);

  const [form, setForm] = useState<Partial<Label>>({
    name: '',
    category: 'CANNABIS',
    layout: 'LANDSCAPE',
    platform: 'NONE',
    qrEnabled: true,
    qrContent: '',
    specs: {},
    primaryColor: '#22c55e',
    secondaryColor: '#064e3b',
    showBorder: true,
    showGradient: true,
  });

  // Fetch products for import dropdown
  const { data: productsData } = useQuery({
    queryKey: ['products-for-labels'],
    queryFn: () => api.get<{ products: Product[] }>('/products?limit=100'),
  });
  const products = productsData?.products || [];

  const categoryConfig = CATEGORIES[form.category || 'CANNABIS'];
  const layoutConfig = LAYOUTS[form.layout || 'LANDSCAPE'];

  // Helper function to map product category to label category
  const mapProductCategoryToLabelCategory = (categoryName?: string | null): keyof typeof CATEGORIES => {
    if (!categoryName) return 'CUSTOM';
    const lower = categoryName.toLowerCase();
    if (lower.includes('cannabis') || lower.includes('flower') || lower.includes('bud')) return 'CANNABIS';
    if (lower.includes('hash') || lower.includes('extract') || lower.includes('concentrate')) return 'HASHISH';
    if (lower.includes('coffee') || lower.includes('caffè') || lower.includes('cafe')) return 'COFFEE';
    if (lower.includes('cacao') || lower.includes('chocolate') || lower.includes('cocoa')) return 'CACAO';
    if (lower.includes('cosmetic') || lower.includes('beauty') || lower.includes('skin')) return 'COSMETICS';
    if (lower.includes('food') || lower.includes('gastro') || lower.includes('edible')) return 'GASTRO';
    return 'CUSTOM';
  };

  // Format price for label display
  const formatPrice = (price: number, currency: string): string => {
    const symbols: Record<string, string> = { EUR: '€', USD: '$', GBP: '£', THB: '฿' };
    const symbol = symbols[currency] || currency;
    return `${symbol}${price.toFixed(2)}`;
  };

  // Format weight for label display
  const formatWeight = (weight?: number | null): string | undefined => {
    if (!weight) return undefined;
    if (weight >= 1) return `${weight}kg`;
    return `${Math.round(weight * 1000)}g`;
  };

  // Import product data into form
  const importFromProduct = useCallback((product: Product) => {
    const labelCategory = mapProductCategoryToLabelCategory(product.categoryName);
    const categoryColor = CATEGORIES[labelCategory].color;
    
    setForm({
      name: product.name,
      category: labelCategory,
      sku: product.code || undefined,
      price: formatPrice(product.price, product.currency),
      weight: formatWeight(product.weight),
      description: product.description || undefined,
      brand: product.store?.name || undefined,
      imageUrl: product.imageUrl || undefined,
      layout: 'LANDSCAPE',
      platform: 'NONE',
      qrEnabled: true,
      qrContent: '',
      specs: {},
      primaryColor: categoryColor,
      secondaryColor: '#1f2937',
      showBorder: true,
      showGradient: true,
    });
    
    setShowProductPicker(false);
    toast.success(`Imported "${product.name}" data`);
  }, []);

  // Check for imported product from navigation state
  useEffect(() => {
    const state = location.state as { importProduct?: Product } | null;
    if (state?.importProduct) {
      importFromProduct(state.importProduct);
      // Clear the state to prevent re-import on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, importFromProduct]);

  // Generate QR code
  useEffect(() => {
    if (form.qrEnabled && form.qrContent) {
      QRCode.toDataURL(form.qrContent, {
        width: 100,
        margin: 1,
        color: { dark: '#000000', light: '#ffffff' },
        errorCorrectionLevel: 'M',
      }).then(setQrDataUrl).catch(() => setQrDataUrl(''));
    } else {
      setQrDataUrl('');
    }
  }, [form.qrEnabled, form.qrContent]);

  // Update colors when category changes
  useEffect(() => {
    const cat = CATEGORIES[form.category || 'CANNABIS'];
    setForm(prev => ({
      ...prev,
      primaryColor: cat.color,
      secondaryColor: form.secondaryColor || '#1f2937',
    }));
  }, [form.category]);

  const createMutation = useMutation({
    mutationFn: (data: Partial<Label>) => api.post('/labels', data),
    onSuccess: () => {
      toast.success('Label saved to library!');
      queryClient.invalidateQueries({ queryKey: ['labels'] });
    },
    onError: () => toast.error('Failed to save label'),
  });

  // Handle drag & drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setForm(prev => ({ ...prev, imageUrl: event.target?.result as string }));
        toast.success('Image uploaded!');
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setForm(prev => ({ ...prev, imageUrl: event.target?.result as string }));
        toast.success('Image uploaded!');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleExport = async (format: 'png' | 'pdf') => {
    if (!previewRef.current) return;
    
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(previewRef.current, {
        backgroundColor: null,
        scale: 3,
        useCORS: true,
      });
      
      if (format === 'png') {
        const link = document.createElement('a');
        link.download = `${form.name || 'label'}_${form.category}_${Date.now()}.png`;
        link.href = canvas.toDataURL('image/png', 1.0);
        link.click();
        toast.success('PNG exported!');
      } else {
        // PDF export using jsPDF
        const { jsPDF } = await import('jspdf');
        const pdf = new jsPDF({
          orientation: layoutConfig.width > layoutConfig.height ? 'landscape' : 'portrait',
          unit: 'px',
          format: [layoutConfig.width * 2, layoutConfig.height * 2],
        });
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, layoutConfig.width * 2, layoutConfig.height * 2);
        pdf.save(`${form.name || 'label'}_${form.category}_${Date.now()}.pdf`);
        toast.success('PDF exported!');
      }
    } catch (error) {
      console.error(error);
      toast.error('Export failed');
    }
  };

  const applyTemplate = (template: typeof TEMPLATES[0]) => {
    setForm(prev => ({
      ...prev,
      category: template.category as keyof typeof CATEGORIES,
      layout: template.layout as keyof typeof LAYOUTS,
      primaryColor: template.primaryColor,
      secondaryColor: template.secondaryColor,
      brand: template.brand,
      slogan: template.slogan,
      specs: {},
    }));
    setShowTemplates(false);
    toast.success(`Template "${template.name}" applied!`);
  };

  const resetForm = () => {
    setForm({
      name: '',
      category: 'CANNABIS',
      layout: 'LANDSCAPE',
      platform: 'NONE',
      qrEnabled: true,
      qrContent: '',
      specs: {},
      primaryColor: '#22c55e',
      secondaryColor: '#064e3b',
      showBorder: true,
      showGradient: true,
    });
    toast.success('Form reset');
  };

  return (
    <div className="space-y-6">
      {/* Templates Modal - Full screen on mobile */}
      <AnimatePresence>
        {showTemplates && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4"
            onClick={() => setShowTemplates(false)}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              className="bg-white rounded-t-2xl sm:rounded-2xl p-4 lg:p-6 w-full sm:max-w-2xl max-h-[85vh] sm:max-h-[80vh] overflow-y-auto border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4 lg:mb-6 sticky top-0 bg-white pb-2">
                <h2 className="text-lg lg:text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Sparkles className="text-amber-500" size={20} />
                  Quick Start Templates
                </h2>
                <button 
                  onClick={() => setShowTemplates(false)} 
                  className="p-2 text-slate-500 hover:text-slate-900 hover:bg-gray-100 rounded-lg active:bg-gray-200"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                {TEMPLATES.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => applyTemplate(template)}
                    className="p-3 lg:p-4 rounded-xl border border-gray-200 hover:border-amber-400 active:border-amber-500 transition-all text-left group"
                    style={{ background: `linear-gradient(135deg, ${template.primaryColor}15, ${template.secondaryColor}25)` }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      {(() => {
                        const CategoryIcon = CATEGORIES[template.category as keyof typeof CATEGORIES].icon;
                        return <CategoryIcon size={22} className="text-slate-700" />;
                      })()}
                      <div>
                        <p className="font-semibold text-slate-900 group-hover:text-amber-600 transition-colors">{template.name}</p>
                        <p className="text-xs text-slate-500">{template.brand}</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">{template.slogan}</p>
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Product Picker Modal - Full screen on mobile */}
      <AnimatePresence>
        {showProductPicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4"
            onClick={() => setShowProductPicker(false)}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              className="bg-white rounded-t-2xl sm:rounded-2xl p-4 lg:p-6 w-full sm:max-w-lg max-h-[85vh] sm:max-h-[80vh] overflow-hidden flex flex-col border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3 lg:mb-4">
                <h2 className="text-lg lg:text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Package className="text-orange-500" size={20} />
                  Import from Product
                </h2>
                <button 
                  onClick={() => setShowProductPicker(false)} 
                  className="p-2 text-slate-500 hover:text-slate-900 hover:bg-gray-100 rounded-lg active:bg-gray-200"
                >
                  <X size={20} />
                </button>
              </div>
              <p className="text-sm text-slate-600 mb-3 lg:mb-4">
                Select a product to import its data into the label editor.
              </p>
              <div className="flex-1 overflow-y-auto space-y-2 -mx-1 px-1">
                {products.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Package size={40} className="mx-auto mb-2 opacity-50" />
                    <p>No products found</p>
                  </div>
                ) : (
                  products.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => importFromProduct(product)}
                      className="w-full p-3 rounded-lg border border-gray-200 hover:border-orange-400 active:border-orange-500 hover:bg-orange-50 active:bg-orange-100 transition-all text-left flex items-center gap-3 min-h-[64px]"
                    >
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <Package size={20} className="text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{product.name}</p>
                        <p className="text-sm text-slate-500">
                          {product.currency === 'EUR' ? '€' : product.currency}
                          {product.price.toFixed(2)}
                          {product.code && <span className="ml-2 font-mono text-xs">#{product.code}</span>}
                        </p>
                      </div>
                      <ChevronDown size={20} className="text-slate-400 -rotate-90 flex-shrink-0" />
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top Actions - Mobile optimized */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        {/* Action buttons - scroll horizontally on mobile */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 sm:pb-0">
          <button
            onClick={() => setShowTemplates(true)}
            className="btn bg-green-500 text-white hover:bg-green-600 shrink-0"
          >
            <Sparkles size={16} />
            <span className="hidden sm:inline">Templates</span>
          </button>
          <button
            onClick={() => setShowProductPicker(true)}
            className="btn bg-orange-500 text-white hover:bg-orange-600 shrink-0"
          >
            <Package size={16} />
            <span className="hidden sm:inline">Import</span>
          </button>
          <button onClick={resetForm} className="btn-ghost shrink-0">
            <RotateCcw size={16} />
            <span className="hidden sm:inline">Reset</span>
          </button>
        </div>
        {/* Save button - full width on mobile */}
        <button
          onClick={() => createMutation.mutate(form)}
          disabled={!form.name}
          className="btn-primary w-full sm:w-auto justify-center"
        >
          <Plus size={16} />
          Save to Library
        </button>
      </div>

      <div className="grid lg:grid-cols-5 gap-4 lg:gap-6">
        {/* Left Panel - Form */}
        <div className="lg:col-span-2 space-y-4">
          {/* Tabs - Scrollable on mobile */}
          <div className="flex gap-1 p-1 bg-white border border-gray-200 rounded-xl overflow-x-auto no-scrollbar">
            {[
              { id: 'basic', label: 'Basic', icon: Type },
              { id: 'design', label: 'Design', icon: Palette },
              { id: 'qr', label: 'QR', icon: Grid3X3 },
              { id: 'export', label: 'Export', icon: Download },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={clsx(
                  'flex-1 flex items-center justify-center gap-1 py-2.5 px-3 rounded-lg text-xs font-medium transition-all shrink-0 min-w-[70px]',
                  activeTab === tab.id
                    ? 'bg-green-500 text-white'
                    : 'text-slate-700 hover:text-slate-900 hover:bg-gray-100'
                )}
              >
                <tab.icon size={16} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="card space-y-4">
            <AnimatePresence mode="wait">
              {activeTab === 'basic' && (
                <motion.div
                  key="basic"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-4"
                >
                  {/* Category */}
                  <div>
                    <label className="label flex items-center gap-2">
                      <Layers size={16} />
                      Category
                    </label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {Object.entries(CATEGORIES).map(([key, cat]) => (
                        <button
                          key={key}
                          onClick={() => setForm({ ...form, category: key as keyof typeof CATEGORIES, specs: {} })}
                          className={clsx(
                            'p-2 rounded-lg text-xs font-medium transition-all flex flex-col items-center gap-1',
                            form.category === key
                              ? 'ring-2 ring-white/50 scale-105'
                              : 'hover:scale-102 opacity-70 hover:opacity-100'
                          )}
                          style={{
                            background: `linear-gradient(135deg, ${cat.color}, ${cat.color}aa)`,
                            color: 'white',
                          }}
                        >
                          <cat.icon size={24} className="flex-shrink-0" />
                          <span className="truncate w-full text-center">{cat.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Product Name & SKU */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label">Product Name *</label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="input"
                        placeholder="e.g. Thai Kush Premium"
                      />
                    </div>
                    <div>
                      <label className="label">SKU / Batch</label>
                      <input
                        type="text"
                        value={form.sku || ''}
                        onChange={(e) => setForm({ ...form, sku: e.target.value })}
                        className="input"
                        placeholder="SKU-001"
                      />
                    </div>
                  </div>

                  {/* Price & Weight */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label">Price</label>
                      <input
                        type="text"
                        value={form.price || ''}
                        onChange={(e) => setForm({ ...form, price: e.target.value })}
                        className="input"
                        placeholder="฿999 / €29.99"
                      />
                    </div>
                    <div>
                      <label className="label">Weight / Volume</label>
                      <input
                        type="text"
                        value={form.weight || ''}
                        onChange={(e) => setForm({ ...form, weight: e.target.value })}
                        className="input"
                        placeholder="100g / 250ml"
                      />
                    </div>
                  </div>

                  {/* Brand & Slogan */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label">Brand</label>
                      <input
                        type="text"
                        value={form.brand || ''}
                        onChange={(e) => setForm({ ...form, brand: e.target.value })}
                        className="input"
                        placeholder="GREENGOLD"
                      />
                    </div>
                    <div>
                      <label className="label">Slogan / Tagline</label>
                      <input
                        type="text"
                        value={form.slogan || ''}
                        onChange={(e) => setForm({ ...form, slogan: e.target.value })}
                        className="input"
                        placeholder="Premium Quality"
                      />
                    </div>
                  </div>

                  {/* Category-specific fields */}
                  <div>
                    <label className="label">Specifications ({categoryConfig.name})</label>
                    <div className="grid grid-cols-2 gap-2">
                      {categoryConfig.fields.map((field) => (
                        <input
                          key={field}
                          type="text"
                          value={form.specs?.[field] || ''}
                          onChange={(e) => setForm({
                            ...form,
                            specs: { ...form.specs, [field]: e.target.value }
                          })}
                          className="input text-sm"
                          placeholder={field}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="label">Description (optional)</label>
                    <textarea
                      value={form.description || ''}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="input min-h-[60px] resize-none"
                      placeholder="Short product description..."
                      rows={2}
                    />
                  </div>
                </motion.div>
              )}

              {activeTab === 'design' && (
                <motion.div
                  key="design"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-4"
                >
                  {/* Image Upload */}
                  <div>
                    <label className="label flex items-center gap-2">
                      <Image size={16} />
                      Product Image
                    </label>
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={clsx(
                        'border-2 border-dashed rounded-xl p-4 text-center transition-all cursor-pointer',
                        isDragging
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                      )}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {form.imageUrl ? (
                        <div className="relative inline-block">
                          <img
                            src={form.imageUrl}
                            alt="Product"
                            className="max-h-24 lg:max-h-32 mx-auto rounded-lg object-contain"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setForm({ ...form, imageUrl: undefined });
                            }}
                            className="absolute -top-2 -right-2 p-1.5 bg-red-500 rounded-full text-white hover:bg-red-600 active:bg-red-700"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="py-3 lg:py-4">
                          <Upload className="mx-auto mb-2 text-slate-400" size={36} />
                          <p className="text-sm text-slate-600">
                            Drag & drop or <span className="text-green-600 font-medium">browse</span>
                          </p>
                          <p className="text-xs text-slate-500 mt-1">PNG, JPG up to 5MB</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    {/* URL Input */}
                    <div className="mt-2 flex gap-2">
                      <input
                        type="url"
                        placeholder="Or paste image URL..."
                        className="input flex-1 text-sm"
                        onBlur={(e) => {
                          if (e.target.value) {
                            setForm({ ...form, imageUrl: e.target.value });
                            e.target.value = '';
                          }
                        }}
                      />
                      <button className="btn-ghost p-2 min-h-[44px] min-w-[44px]">
                        <Link size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Layout - scrollable on mobile */}
                  <div>
                    <label className="label flex items-center gap-2">
                      <Layout size={16} />
                      Layout
                    </label>
                    <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1 lg:grid lg:grid-cols-5 lg:overflow-visible">
                      {Object.entries(LAYOUTS).map(([key, layout]) => (
                        <button
                          key={key}
                          onClick={() => setForm({ ...form, layout: key as keyof typeof LAYOUTS })}
                          className={clsx(
                            'p-2.5 lg:p-3 rounded-lg text-xs font-medium transition-all flex flex-col items-center gap-1 shrink-0 min-w-[60px]',
                            form.layout === key
                              ? 'bg-[#4A7C2E] text-white'
                              : 'bg-gray-100 text-slate-700 hover:bg-gray-200 active:bg-gray-300 border border-gray-200'
                          )}
                        >
                          <layout.icon size={20} />
                          <span className="text-[10px] whitespace-nowrap">{layout.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Colors - stack on mobile */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                    <div>
                      <label className="label">Primary Color</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={form.primaryColor}
                          onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
                          className="w-12 h-11 rounded-lg cursor-pointer border border-gray-200"
                        />
                        <input
                          type="text"
                          value={form.primaryColor}
                          onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
                          className="input flex-1 font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="label">Secondary Color</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={form.secondaryColor}
                          onChange={(e) => setForm({ ...form, secondaryColor: e.target.value })}
                          className="w-12 h-11 rounded-lg cursor-pointer border border-gray-200"
                        />
                        <input
                          type="text"
                          value={form.secondaryColor}
                          onChange={(e) => setForm({ ...form, secondaryColor: e.target.value })}
                          className="input flex-1 font-mono text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Style Options - larger touch targets */}
                  <div className="flex flex-wrap gap-3 lg:gap-4">
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer py-1">
                      <input
                        type="checkbox"
                        checked={form.showBorder}
                        onChange={(e) => setForm({ ...form, showBorder: e.target.checked })}
                        className="rounded w-5 h-5"
                      />
                      Show Border
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer py-1">
                      <input
                        type="checkbox"
                        checked={form.showGradient}
                        onChange={(e) => setForm({ ...form, showGradient: e.target.checked })}
                        className="rounded w-5 h-5"
                      />
                      Gradient Background
                    </label>
                  </div>

                  {/* Platform - scrollable on mobile */}
                  <div>
                    <label className="label">Platform Optimization</label>
                    <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1 lg:grid lg:grid-cols-5 lg:overflow-visible">
                      {Object.entries(PLATFORMS).map(([key, platform]) => (
                        <button
                          key={key}
                          onClick={() => setForm({ ...form, platform: key as keyof typeof PLATFORMS })}
                          className={clsx(
                            'p-2.5 rounded-lg text-xs font-medium transition-all flex flex-col items-center gap-1 shrink-0 min-w-[60px] min-h-[56px]',
                            form.platform === key
                              ? 'bg-[#4A7C2E] text-white'
                              : 'bg-gray-100 text-slate-700 hover:bg-gray-200 active:bg-gray-300 border border-gray-200'
                          )}
                        >
                          {platform.icon ? (
                            <platform.icon size={18} />
                          ) : (
                            <FileText size={18} />
                          )}
                          <span className="text-[10px] whitespace-nowrap">{platform.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === 'qr' && (
                <motion.div
                  key="qr"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <label className="label mb-0 flex items-center gap-2">
                      <Grid3X3 size={16} />
                      QR Code
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.qrEnabled}
                        onChange={(e) => setForm({ ...form, qrEnabled: e.target.checked })}
                        className="rounded w-5 h-5"
                      />
                      <span className={form.qrEnabled ? 'text-green-600 font-medium' : 'text-slate-500'}>
                        {form.qrEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </label>
                  </div>

                  {form.qrEnabled && (
                    <>
                      <div>
                        <label className="label">QR Content</label>
                        <input
                          type="text"
                          value={form.qrContent || ''}
                          onChange={(e) => setForm({ ...form, qrContent: e.target.value })}
                          className="input"
                          placeholder="https://your-website.com/product/123"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Enter URL, text, or any data to encode
                        </p>
                      </div>

                      {/* QR Preview */}
                      {qrDataUrl && (
                        <div className="flex justify-center">
                          <div className="p-4 bg-white rounded-xl">
                            <img src={qrDataUrl} alt="QR Preview" className="w-32 h-32" />
                          </div>
                        </div>
                      )}

                      {/* Quick QR Links */}
                      <div>
                        <label className="label">Quick Links</label>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { label: 'Product Page', value: `https://greengold.th/p/${form.sku || 'SKU'}` },
                            { label: 'Traceability', value: `https://trace.greengold.th/${form.sku || 'SKU'}` },
                            { label: 'WhatsApp Order', value: `https://wa.me/66xxxxxxxxx?text=Order: ${form.name}` },
                            { label: 'Instagram', value: `https://instagram.com/greengold_th` },
                          ].map((link) => (
                            <button
                              key={link.label}
                              onClick={() => setForm({ ...form, qrContent: link.value })}
                              className="btn-ghost text-xs py-2"
                            >
                              {link.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </motion.div>
              )}

              {activeTab === 'export' && (
                <motion.div
                  key="export"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-4"
                >
                  <div className="text-center py-4">
                    <Eye className="mx-auto mb-3 text-slate-400" size={40} />
                    <h3 className="font-semibold text-slate-900 mb-1">Export Your Label</h3>
                    <p className="text-sm text-slate-500">
                      Download in high-quality formats
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-2 lg:gap-3">
                    <button
                      onClick={() => handleExport('png')}
                      className="p-3 lg:p-4 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 text-white hover:from-blue-600 hover:to-cyan-700 active:from-blue-700 active:to-cyan-800 transition-all flex flex-col items-center gap-1 lg:gap-2 min-h-[88px]"
                    >
                      <Image size={24} />
                      <span className="font-semibold text-sm lg:text-base">PNG</span>
                      <span className="text-xs opacity-80 hidden sm:block">High-res image</span>
                    </button>
                    <button
                      onClick={() => handleExport('pdf')}
                      className="p-3 lg:p-4 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 text-white hover:from-red-600 hover:to-orange-700 active:from-red-700 active:to-orange-800 transition-all flex flex-col items-center gap-1 lg:gap-2 min-h-[88px]"
                    >
                      <FileText size={24} />
                      <span className="font-semibold text-sm lg:text-base">PDF</span>
                      <span className="text-xs opacity-80 hidden sm:block">Print-ready</span>
                    </button>
                  </div>

                  <div className="p-3 lg:p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <h4 className="font-medium text-slate-900 mb-2 text-sm lg:text-base">Export Info</h4>
                    <ul className="text-xs lg:text-sm text-slate-700 space-y-1">
                      <li>• PNG: 3x resolution for crisp printing</li>
                      <li>• PDF: Vector-ready for professional printing</li>
                      <li>• Size: {layoutConfig.width}×{layoutConfig.height}px</li>
                    </ul>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Right Panel - Preview */}
        <div className="lg:col-span-3 space-y-4 lg:sticky lg:top-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-slate-700 flex items-center gap-2">
              <Eye size={16} />
              Preview
            </h3>
            <span className="text-xs text-slate-500">
              {layoutConfig.width}×{layoutConfig.height}px
            </span>
          </div>

          {/* Preview container - scales on mobile */}
          <div className="flex items-center justify-center bg-gray-50 rounded-2xl p-4 lg:p-8 min-h-[300px] lg:min-h-[500px] border border-gray-200 overflow-auto">
            <div
              ref={previewRef}
              className="relative rounded-xl shadow-2xl overflow-hidden transition-all duration-300"
              style={{
                width: layoutConfig.width,
                height: layoutConfig.height,
                background: form.showGradient
                  ? `linear-gradient(135deg, ${form.primaryColor}15, ${form.secondaryColor}30)`
                  : '#ffffff',
                border: form.showBorder ? `3px solid ${form.primaryColor}` : 'none',
              }}
            >
              {/* Background Image */}
              {form.imageUrl && (
                <div
                  className="absolute inset-0 opacity-20"
                  style={{
                    backgroundImage: `url(${form.imageUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
              )}

              {/* Top Color Bar */}
              <div
                className="h-2 w-full"
                style={{ backgroundColor: form.primaryColor }}
              />

              <div className="relative p-5 h-full flex flex-col">
                {/* Header: Brand & Category */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {(() => {
                      const CategoryIcon = categoryConfig.icon;
                      return <CategoryIcon size={24} />;
                    })()}
                    <span
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-bold uppercase"
                      style={{ backgroundColor: form.primaryColor, color: 'white' }}
                    >
                      {categoryConfig.name}
                    </span>
                  </div>
                  {form.sku && (
                    <span className="text-xs text-slate-500 font-mono">#{form.sku}</span>
                  )}
                </div>

                {/* Brand */}
                {form.brand && (
                  <p
                    className="text-xs font-bold tracking-widest mb-1"
                    style={{ color: form.primaryColor }}
                  >
                    {form.brand}
                  </p>
                )}

                {/* Product Name */}
                <h2 className="text-2xl font-bold text-slate-900 mb-1 leading-tight">
                  {form.name || 'Product Name'}
                </h2>

                {/* Slogan */}
                {form.slogan && (
                  <p className="text-sm text-slate-600 italic mb-3">{form.slogan}</p>
                )}

                {/* Specs */}
                <div className="flex flex-wrap gap-1.5 mb-auto">
                  {Object.entries(form.specs || {}).map(([key, value]) =>
                    value ? (
                      <span
                        key={key}
                        className="text-xs px-2 py-1 rounded-full"
                        style={{
                          backgroundColor: `${form.primaryColor}20`,
                          color: form.primaryColor,
                          border: `1px solid ${form.primaryColor}40`,
                        }}
                      >
                        <strong>{key}:</strong> {value}
                      </span>
                    ) : null
                  )}
                </div>

                {/* Bottom Row */}
                <div className="flex items-end justify-between mt-3 pt-3 border-t border-slate-200">
                  <div>
                    {form.price && (
                      <p
                        className="text-3xl font-bold"
                        style={{ color: form.primaryColor }}
                      >
                        {form.price}
                      </p>
                    )}
                    {form.weight && (
                      <p className="text-sm text-slate-500">{form.weight}</p>
                    )}
                  </div>

                  {/* Product Image */}
                  {form.imageUrl && (
                    <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-white shadow-lg">
                      <img
                        src={form.imageUrl}
                        alt="Product"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* QR Code */}
                  {form.qrEnabled && qrDataUrl && (
                    <div className="bg-white p-1 rounded-lg shadow-md">
                      <img src={qrDataUrl} alt="QR" className="w-14 h-14" />
                    </div>
                  )}
                </div>

                {/* Platform Badge */}
                {form.platform && form.platform !== 'NONE' && PLATFORMS[form.platform]?.badge && (
                  <div
                    className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm shadow-lg"
                    style={{ backgroundColor: PLATFORMS[form.platform]?.badge || '#6366f1' }}
                  >
                    {(() => {
                      const PlatformIcon = PLATFORMS[form.platform]?.icon;
                      return PlatformIcon ? <PlatformIcon size={16} /> : null;
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// LABELS LIST COMPONENT
// ============================================

function LabelsList() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<string>('ALL');
  
  const { data, isLoading } = useQuery({
    queryKey: ['labels'],
    queryFn: () => api.get<{ labels: Label[] }>('/labels'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/labels/${id}`),
    onSuccess: () => {
      toast.success('Label deleted');
      queryClient.invalidateQueries({ queryKey: ['labels'] });
    },
  });

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  const labels = data?.labels || [];
  const filteredLabels = filter === 'ALL' ? labels : labels.filter(l => l.category === filter);

  if (labels.length === 0) {
    return (
      <div className="text-center py-12 lg:py-16">
        <Tags size={48} className="lg:w-16 lg:h-16 mx-auto mb-4 text-slate-400" />
        <h3 className="text-lg lg:text-xl font-semibold text-slate-900 mb-2">No Labels Yet</h3>
        <p className="text-slate-500 mb-6">Create your first professional label</p>
        <NavLink to="/labels" className="btn bg-green-500 text-white hover:bg-green-600 active:bg-green-700 inline-flex">
          <Plus size={16} />
          Create Label
        </NavLink>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter - horizontal scroll on mobile */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 -mx-1 px-1">
        <button
          onClick={() => setFilter('ALL')}
          className={clsx(
            'inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all shrink-0 min-h-[32px]',
            filter === 'ALL' ? 'bg-[#4A7C2E] text-white' : 'bg-gray-100 text-slate-700 hover:bg-gray-200 active:bg-gray-300 border border-gray-200'
          )}
        >
          All ({labels.length})
        </button>
        {Object.entries(CATEGORIES).map(([key, cat]) => {
          const count = labels.filter(l => l.category === key).length;
          if (count === 0) return null;
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={clsx(
                'inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all shrink-0 min-h-[32px]',
                filter === key ? 'text-white' : 'bg-gray-100 text-slate-700 hover:bg-gray-200 active:bg-gray-300 border border-gray-200'
              )}
              style={filter === key ? { backgroundColor: cat.color } : {}}
            >
              <cat.icon size={14} />
              {cat.name} ({count})
            </button>
          );
        })}
      </div>

      {/* Grid - Mobile optimized */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-4">
        {filteredLabels.map((label) => {
          const cat = CATEGORIES[label.category];
          return (
            <motion.div
              key={label.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card group hover:border-gray-300 transition-all p-3 lg:p-4"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <cat.icon size={20} className="text-slate-600" />
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold uppercase"
                    style={{ backgroundColor: cat.color, color: 'white' }}
                  >
                    {cat.name}
                  </span>
                </div>
                {/* Action buttons - always visible on mobile */}
                <div className="flex gap-1 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                  <button className="p-2 lg:p-1.5 rounded bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-slate-700 border border-gray-200">
                    <Edit size={16} />
                  </button>
                  <button className="p-2 lg:p-1.5 rounded bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-slate-700 border border-gray-200">
                    <Copy size={16} />
                  </button>
                  <button
                    onClick={() => deleteMutation.mutate(label.id)}
                    className="p-2 lg:p-1.5 rounded bg-gray-100 hover:bg-red-500 active:bg-red-600 text-slate-700 hover:text-white border border-gray-200"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {label.imageUrl && (
                <div className="h-20 lg:h-24 rounded-lg overflow-hidden mb-3 bg-gray-100 border border-gray-200">
                  <img src={label.imageUrl} alt={label.name} className="w-full h-full object-cover" />
                </div>
              )}

              <h3 className="font-semibold text-slate-900 mb-1 truncate">{label.name}</h3>
              {label.brand && <p className="text-xs text-slate-500 mb-2">{label.brand}</p>}
              
              {label.price && (
                <p className="text-lg lg:text-xl font-bold mb-2" style={{ color: cat.color }}>{label.price}</p>
              )}

              <div className="flex flex-wrap gap-1">
                {Object.entries(label.specs || {}).slice(0, 2).map(([key, value]) => (
                  <span key={key} className="text-xs px-1.5 py-0.5 bg-gray-100 text-slate-700 rounded border border-gray-200">
                    {value}
                  </span>
                ))}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// IMPORT CSV COMPONENT
// ============================================

function ImportCSV() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<Record<string, string>[]>([]);
  const [importing, setImporting] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const csv = event.target?.result as string;
      const lines = csv.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim());
      
      const data = lines.slice(1).map(line => {
        const cols = line.split(',').map(c => c.trim());
        const row: Record<string, string> = {};
        headers.forEach((h, i) => {
          row[h] = cols[i] || '';
        });
        return row;
      });
      
      setPreview(data.slice(0, 5));
      toast.success(`Found ${data.length} products`);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (preview.length === 0) return;
    setImporting(true);
    
    try {
      // Create labels from preview data
      for (const row of preview) {
        await api.post('/labels', {
          name: row['Name'] || row['Product Name'] || 'Untitled',
          category: 'CUSTOM',
          sku: row['SKU'] || '',
          price: row['Price'] || '',
          weight: row['Weight'] || '',
          layout: 'LANDSCAPE',
          platform: 'NONE',
          qrEnabled: true,
          primaryColor: '#6366f1',
          secondaryColor: '#1f2937',
        });
      }
      
      toast.success(`Imported ${preview.length} labels!`);
      queryClient.invalidateQueries({ queryKey: ['labels'] });
      setPreview([]);
    } catch (error) {
      toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="text-center py-6 lg:py-8">
        <FileSpreadsheet className="mx-auto mb-4 text-slate-400" size={40} />
        <h3 className="text-lg lg:text-xl font-semibold text-slate-900 mb-2">Import from CSV</h3>
        <p className="text-slate-500 mb-6 text-sm lg:text-base">Bulk create labels from spreadsheet data</p>
        
        <button
          onClick={() => fileInputRef.current?.click()}
          className="btn bg-green-500 text-white hover:bg-green-600 active:bg-green-700"
        >
          <Upload size={16} />
          Select CSV File
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {preview.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium text-slate-900 text-sm lg:text-base">Preview (first 5 rows)</h4>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-xs lg:text-sm min-w-[500px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  {Object.keys(preview[0]).map(key => (
                    <th key={key} className="text-left p-2 lg:p-3 text-slate-600 font-medium whitespace-nowrap">{key}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((row, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    {Object.values(row).map((val, j) => (
                      <td key={j} className="p-2 lg:p-3 text-slate-700 whitespace-nowrap">{val}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <button
            onClick={handleImport}
            disabled={importing}
            className="btn-primary w-full min-h-[44px]"
          >
            {importing ? 'Importing...' : `Import ${preview.length} Labels`}
          </button>
        </div>
      )}

      <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
        <h4 className="font-medium text-slate-900 mb-2">CSV Format</h4>
        <p className="text-sm text-slate-700 mb-2">Your CSV should include these columns:</p>
        <code className="block text-xs bg-white p-3 rounded-lg text-[#4A7C2E] border border-gray-300">
          Name, SKU, Price, Weight, Description
        </code>
      </div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function LabelForge() {
  return (
    <div className="max-w-7xl mx-auto space-y-4 lg:space-y-6">
      {/* Header - Compact on mobile */}
      <div className="flex items-center gap-3 lg:gap-4">
        <div className="p-2.5 lg:p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg shadow-green-500/20">
          <Tags size={20} className="lg:w-6 lg:h-6 text-white" />
        </div>
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-900">LabelForge Pro</h1>
          <p className="text-sm lg:text-base text-slate-600 hidden sm:block">Professional label designer</p>
        </div>
      </div>

      {/* Navigation - Horizontal scroll on mobile */}
      <div className="flex gap-2 overflow-x-auto pb-4 border-b border-gray-200 no-scrollbar -mx-4 px-4 lg:mx-0 lg:px-0">
        <NavLink
          to="/labels"
          end
          className={({ isActive }) =>
            clsx('btn shrink-0 py-2.5 px-4', isActive ? 'bg-green-500 text-white' : 'btn-ghost')
          }
        >
          <Plus size={18} />
          <span>Create</span>
        </NavLink>
        <NavLink
          to="/labels/list"
          className={({ isActive }) =>
            clsx('btn shrink-0 py-2.5 px-4', isActive ? 'bg-green-500 text-white' : 'btn-ghost')
          }
        >
          <List size={18} />
          <span>My Labels</span>
        </NavLink>
        <NavLink
          to="/labels/import"
          className={({ isActive }) =>
            clsx('btn shrink-0 py-2.5 px-4', isActive ? 'bg-green-500 text-white' : 'btn-ghost')
          }
        >
          <FileSpreadsheet size={18} />
          <span>Import</span>
        </NavLink>
      </div>

      {/* Content - Less padding on mobile */}
      <div className="card p-3 lg:p-6">
        <Routes>
          <Route index element={<LabelEditor />} />
          <Route path="list" element={<LabelsList />} />
          <Route path="import" element={<ImportCSV />} />
        </Routes>
      </div>
    </div>
  );
}
